<script setup lang="ts">
import { ref } from 'vue';
import api from '../api';

const email   = ref('');
const loading = ref(false);
const sent    = ref(false);
const error   = ref('');

const submit = async () => {
  if (!email.value) { error.value = 'Please enter your email address.'; return; }
  loading.value = true;
  error.value   = '';
  try {
    await api.post('customer/auth/password/forgot', { email: email.value });
    sent.value = true;
  } catch {
    error.value = 'Something went wrong. Please try again.';
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="auth-page">
    <AuthSlideshow />
    <div class="auth-right">
      <div class="form-container">

        <div v-if="sent" class="success-block">
          <div class="success-icon">✓</div>
          <h2 class="form-title">Check your email</h2>
          <p class="form-subtitle">We've sent reset instructions to <strong>{{ email }}</strong>.</p>
          <router-link to="/login" class="back-link">← Back to sign in</router-link>
        </div>

        <template v-else>
          <div class="form-header">
            <h2 class="form-title">Reset your password</h2>
            <p class="form-subtitle">Enter your email and we'll send you a reset link.</p>
          </div>
          <form class="auth-form" @submit.prevent="submit">
            <AppAlert :message="error" />
            <div class="field-group">
              <label class="field-label">Email address</label>
              <el-input v-model="email" placeholder="you@company.com" size="large" />
            </div>
            <button class="submit-btn" type="submit" :disabled="loading">
              <span v-if="!loading">Send reset link</span><span v-else>Sending…</span>
            </button>
            <div class="bottom-link">
              <router-link to="/login" class="nav-link">← Back to sign in</router-link>
            </div>
          </form>
        </template>

      </div>
    </div>
  </div>
</template>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display&display=swap');

.auth-page { display: flex; min-height: 100vh; font-family: 'DM Sans', sans-serif; }

.auth-right {
  flex: 1; min-width: 0; display: flex; align-items: center;
  justify-content: center; background: #fafafa; padding: 3rem 2rem;
}

.form-container { width: 100%; max-width: 400px; animation: fadeUp 0.4s ease both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.form-header { margin-bottom: 2.5rem; }
.form-title { font-family: 'DM Serif Display', serif; font-size: 2rem; font-weight: 400; color: #0f1c2e; margin: 0 0 0.5rem 0; letter-spacing: -0.02em; }
.form-subtitle { font-size: 0.9rem; color: #8a94a6; margin: 0; line-height: 1.6; }

.field-group { margin-bottom: 1.25rem; }
.label-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.4rem; }
.field-label { display: block; font-size: 0.82rem; font-weight: 500; color: #374151; margin-bottom: 0.4rem; letter-spacing: 0.01em; }
.forgot-link { font-size: 0.8rem; color: #0061eb; text-decoration: none; opacity: 0.7; transition: opacity 0.15s; }
.forgot-link:hover { opacity: 1; }

.auth-form :deep(.ep-input__wrapper),
.auth-form :deep(.el-input__wrapper) { border-radius: 8px !important; border: 1.5px solid #e5e7eb !important; box-shadow: none !important; background: white !important; transition: border-color 0.2s; }
.auth-form :deep(.ep-input__wrapper:hover),
.auth-form :deep(.el-input__wrapper:hover) { border-color: #b0bec5 !important; }
.auth-form :deep(.ep-input__wrapper.is-focus),
.auth-form :deep(.el-input__wrapper.is-focus) { border-color: #0061eb !important; }
.auth-form :deep(.ep-form-item),
.auth-form :deep(.el-form-item) { margin-bottom: 0; }
.auth-form :deep(input:-webkit-autofill),
.auth-form :deep(input:-webkit-autofill:hover),
.auth-form :deep(input:-webkit-autofill:focus) { -webkit-box-shadow: 0 0 0px 1000px white inset !important; box-shadow: 0 0 0px 1000px white inset !important; -webkit-text-fill-color: #374151 !important; transition: background-color 5000s ease-in-out 0s; }

.phone-input { width: 100%; }
.auth-form :deep(.m-phone-number-input) { width: 100%; }

.terms-group { margin-top: 0.5rem; }
.terms-label { display: flex; align-items: flex-start; gap: 0.6rem; cursor: pointer; }
.terms-text { font-size: 0.84rem; color: #374151; line-height: 1.5; margin-top: 2px; }
.terms-link { color: #0061eb; text-decoration: underline; }

.submit-btn { width: 100%; height: 46px; background: #0061eb; color: white; border: none; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 500; cursor: pointer; margin-top: 0.75rem; transition: background 0.2s, transform 0.1s; }
.submit-btn:hover:not(:disabled) { background: #0050c7; }
.submit-btn:active:not(:disabled) { transform: scale(0.99); }
.submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.bottom-link { text-align: center; margin-top: 1.75rem; font-size: 0.87rem; color: #8a94a6; padding-bottom: 1rem; }
.nav-link-inline { color: #0061eb; font-weight: 500; text-decoration: none; margin-left: 0.25rem; }
.nav-link-inline:hover { text-decoration: underline; }
.nav-link { font-size: 0.87rem; color: #0061eb; text-decoration: none; opacity: 0.7; transition: opacity 0.15s; }
.nav-link:hover { opacity: 1; }

.success-block { text-align: center; animation: fadeUp 0.4s ease both; }
.success-icon { width: 56px; height: 56px; background: #0061eb; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; margin: 0 auto 1.5rem; }
.success-block .form-title { margin-bottom: 0.75rem; }
.success-block .form-subtitle { margin-bottom: 2rem; }
.back-link { font-size: 0.87rem; color: #0061eb; text-decoration: none; font-weight: 500; }
.back-link:hover { text-decoration: underline; }

@media (max-width: 900px) {
  .auth-right { background: white; }
}
</style>